import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Activity, Sparkles, TrendingUp, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import PageHeader from '@/components/PageHeader';
import StatCard from '@/components/StatCard';
import { getMedications, getMotorTests, getDailyReports } from '@/utils/api';

export default function Dashboard({ patient }) {
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [recentTests, setRecentTests] = useState([]);
  const [todayReport, setTodayReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [medsRes, testsRes, reportsRes] = await Promise.all([
          getMedications(patient.id),
          getMotorTests(patient.id),
          getDailyReports(patient.id),
        ]);

        setMedications(medsRes.data);
        setRecentTests(testsRes.data.slice(0, 5));
        
        const today = new Date().toISOString().split('T')[0];
        const todayData = reportsRes.data.find((r) => r.data === today);
        setTodayReport(todayData);
      } catch (error) {
        console.error('Errore nel caricamento dati:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [patient.id]);

  const adherencePercentage = medications.length > 0 ? 85 : 0;

  return (
    <div className="page-container pb-24">
      <PageHeader
        title={`Ciao, ${patient.nome}`}
        subtitle={new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
        icon={null}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard
          title="Farmaci Attivi"
          value={medications.length}
          subtitle="Gestione ottimale"
          icon={Activity}
          color="blue"
          delay={0.1}
        />
        <StatCard
          title="Test Completati"
          value={recentTests.length}
          subtitle="Ultima settimana"
          icon={TrendingUp}
          color="green"
          delay={0.2}
        />
        <StatCard
          title="Stato Oggi"
          value={todayReport?.stato || 'Non registrato'}
          subtitle="Report giornaliero"
          icon={Calendar}
          color="orange"
          delay={0.3}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-slate-200/60 mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">Aderenza Terapeutica</h2>
          <span className="text-2xl font-bold text-blue-600">{adherencePercentage}%</span>
        </div>
        <Progress value={adherencePercentage} className="h-3 mb-2" />
        <p className="text-sm text-slate-600">Ottimo lavoro! Continua così.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 soft-shadow-lg text-white mb-6"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Inizia un Test Motorio</h2>
            <p className="text-blue-100 text-base">Monitora le tue performance quotidiane</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
            <Activity className="w-8 h-8" />
          </div>
        </div>
        <Button
          data-testid="btn-inizia-test"
          onClick={() => navigate('/test-motori')}
          className="w-full min-h-[64px] text-lg font-semibold rounded-2xl bg-white text-blue-600 hover:bg-blue-50 shadow-lg transition-all duration-300 active:scale-[0.98]"
        >
          Inizia Test
          <ArrowRight className="w-6 h-6 ml-2" />
        </Button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.6 }}
        className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-3xl p-8 soft-shadow-lg text-white"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Insights AI</h2>
            <p className="text-indigo-100 text-base">Analisi personalizzate sui tuoi progressi</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
            <Sparkles className="w-8 h-8" />
          </div>
        </div>
        <Button
          data-testid="btn-vedi-insights"
          onClick={() => navigate('/ai-insights')}
          className="w-full min-h-[64px] text-lg font-semibold rounded-2xl bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg transition-all duration-300 active:scale-[0.98]"
        >
          Vedi Insights
          <Sparkles className="w-6 h-6 ml-2" />
        </Button>
      </motion.div>
    </div>
  );
}
